import { RoleType } from "../../types/enums/RoleType";
import { CompanyInfo, CompanyRole, CompanyroleId } from "../../types/CompanyInfo";
import {
  boardRoles,
  getFullName,
  isBoardMember,
  isDeputyBoardMember,
  isObserver,
  sortedBoard,
} from "../../utils/Roles";
import { CompanyRoleIdWithName, ProjectJsonData, Recipient } from "./commonDomain";
import {
  atNoon,
  plusDays,
  plusHours,
  toDate,
  toLocalDateISOString,
  toLocalDateTimeISOString,
  toLocalTime,
} from "../../utils/Dates";
import { BoardMeetingForm } from "../noticeBoardMeeting/noticeBoardMeetingDomain";
import { identity, sortBy } from "lodash";
import { SignersMode } from "../boardMeetingMinutes/boardMeetingMinutesDomain";
import { TMessage } from "../../i18n/tt";
import { MeetingPlace } from "../../meetings/meetingsDomain";
import { isBlank } from "../../utils/strings";
import { AIClient } from "../../ai/AIAssistant";
import { Language } from "../../i18n/languages";
import {
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "../../common/commonForms";
import { LocalizedText } from "../../common/common";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";

export type BoardMeeting = {
  useMeetingNumber: boolean;
  boardMeetingNumber?: string;
  meetingStartDate: string;
  meetingEndDate: string;
  boardMeetingForm: BoardMeetingForm;
  meetingPlace?: MeetingPlace;
  inlineAddress?: string;
  noticeDescription?: LocalizedText[];
  meetingPlaceholderVisible?: boolean;
  meetingPlaceholderIncludesAgenda?: boolean;
};

export function createBoardMeeting(data?: Partial<BoardMeeting>): BoardMeeting {
  return {
    ...data,
    useMeetingNumber: data?.useMeetingNumber ?? false,
    meetingStartDate: data?.meetingStartDate ?? defaultBoardMeetingStartDate(),
    meetingEndDate: data?.meetingEndDate ?? defaultBoardMeetingEndDate(),
    boardMeetingForm: data?.boardMeetingForm ?? BoardMeetingForm.PHYSICAL,
  };
}

export function defaultBoardMeetingStartDate() {
  return toLocalDateTimeISOString(atNoon(Date.now()));
}

export function defaultBoardMeetingEndDate() {
  return toLocalDateTimeISOString(plusHours(defaultBoardMeetingStartDate(), 1));
}

export type BoardMeetingFields = {
  boardMeetingNumber: StringFieldModel;
  meetingStartDate: LocalDateFieldModel;
  meetingStartTime: TimeFieldModel;
  meetingEndTime: TimeFieldModel;
  boardMeetingForm: RadioFieldModel<BoardMeetingForm>;
  meetingPlace: RadioFieldModel<MeetingPlace>;
  inlineAddress: StringFieldModel;
  noticeDescription: LocalizedTextEditorModel;
  isChanged?: boolean;
  meetingPlaceholderState: RadioFieldModel<"hidden" | "visible" | "visible-with-agenda">;
};

const toMeetingPlaceholderState = ({
  meetingPlaceholderVisible,
  meetingPlaceholderIncludesAgenda,
}: { meetingPlaceholderVisible?: boolean; meetingPlaceholderIncludesAgenda?: boolean } = {}):
  | "hidden"
  | "visible"
  | "visible-with-agenda" =>
  meetingPlaceholderVisible && meetingPlaceholderIncludesAgenda
    ? "visible-with-agenda"
    : meetingPlaceholderVisible
      ? "visible"
      : "hidden";

export function createBoardMeetingFields({
  onChange,
  jsonData,
  aiClient,
  isLocked,
  defaultDocumentLanguage,
}: {
  onChange: () => void;
  jsonData: ProjectJsonData;
  aiClient: AIClient;
  isLocked: boolean;
  defaultDocumentLanguage: Language[];
}): BoardMeetingFields {
  const boardMeeting = jsonData.boardMeeting ?? createBoardMeeting();
  return {
    boardMeetingNumber: new StringFieldModel({
      value: boardMeeting.boardMeetingNumber,
      onChange,
    }),
    meetingStartDate: new LocalDateFieldModel({
      value: toLocalDateISOString(boardMeeting.meetingStartDate),
      required: true,
      onChange,
    }),
    meetingStartTime: new TimeFieldModel({
      value: toLocalTime(boardMeeting.meetingStartDate),
      required: true,
      onChange,
    }),
    meetingEndTime: new TimeFieldModel({
      value: toLocalTime(boardMeeting.meetingEndDate),
      required: true,
      onChange,
    }),
    boardMeetingForm: new RadioFieldModel<BoardMeetingForm>({
      value: boardMeeting.boardMeetingForm,
      options: Object.values(BoardMeetingForm),
      idFn: identity,
      labelFn: (v) => {
        switch (v) {
          case BoardMeetingForm.PHYSICAL:
            return { key: "documents.meetingForm.physical" };
          case BoardMeetingForm.ELECTRONIC:
            return { key: "documents.meetingForm.electronic" };
          case BoardMeetingForm.HYBRID:
            return { key: "documents.meetingForm.hybrid" };
          case BoardMeetingForm.CIRCULATION:
            return { key: "documents.meetingForm.circulation" };
        }
      },
      onChange,
    }),
    meetingPlace: new RadioFieldModel<MeetingPlace>({
      value: boardMeeting.meetingPlace ?? MeetingPlace.companysOffice,
      options: Object.values(MeetingPlace),
      idFn: identity,
      labelFn: (v) => {
        switch (v) {
          case MeetingPlace.companysOffice:
            return { key: "documents.meetingForm.companys_office" };
          case MeetingPlace.inlineAddress:
            return { key: "documents.meetingForm.inline_address" };
        }
      },
      onChange,
    }),
    inlineAddress: new StringFieldModel({ value: boardMeeting.inlineAddress, onChange }),
    noticeDescription: new LocalizedTextEditorModel({
      texts:
        boardMeeting.noticeDescription ??
        defaultDocumentLanguage.map((language) => ({ text: "", language })),

      isReadOnly: () => isLocked,
      label: { key: "documents.notice.description" },
      aiClient,
      onChange,
    }),
    meetingPlaceholderState: new RadioFieldModel<"hidden" | "visible" | "visible-with-agenda">({
      onChange,
      options: ["hidden", "visible", "visible-with-agenda"],
      value: toMeetingPlaceholderState(jsonData?.boardMeeting),
      idFn: identity,
      iconFn: (v) => {
        switch (v) {
          case "hidden":
            return { name: "circle", color: "subtle", size: "sm" };
          case "visible":
            return { name: "calendar", color: "subtle", size: "sm" };
          case "visible-with-agenda":
            return { name: "circle", weight: "fill", color: "success", size: "sm" };
        }
      },
      labelFn: (v) => {
        switch (v) {
          case "hidden":
            return { key: "board-meeting.placeholder.hidden" };
          case "visible":
            return { key: "board-meeting.placeholder.visible" };
          case "visible-with-agenda":
            return { key: "board-meeting.placeholder.visible-with-agenda" };
        }
      },
      descriptionFn: (v) => {
        switch (v) {
          case "hidden":
            return { key: "board-meeting.placeholder.hidden.description" };
          case "visible":
            return { key: "board-meeting.placeholder.visible.description" };
          case "visible-with-agenda":
            return { key: "board-meeting.placeholder.visible-with-agenda.description" };
        }
      },
    }),
  };
}

export function prepareMeetingDateForSave(fields: {
  meetingStartDate: LocalDateFieldModel;
  meetingStartTime: TimeFieldModel;
  meetingEndTime: TimeFieldModel;
}): {
  meetingStartDate: string;
  meetingEndDate: string;
} {
  const startDate = toDate(
    `${fields.meetingStartDate.validatedValue}T${fields.meetingStartTime.validatedValue}`,
  );
  const endDate = toDate(
    `${fields.meetingStartDate.validatedValue}T${fields.meetingEndTime.validatedValue}`,
  );
  return {
    meetingStartDate: toLocalDateTimeISOString(startDate),
    meetingEndDate: toLocalDateTimeISOString(endDate < startDate ? plusDays(endDate, 1) : endDate),
  };
}

const prepareMeetingPlaceholderForSave = ({
  meetingPlaceholderState,
}: BoardMeetingFields): {
  meetingPlaceholderVisible?: boolean;
  meetingPlaceholderIncludesAgenda?: boolean;
} => {
  switch (meetingPlaceholderState.value) {
    case "hidden":
      return {};
    case "visible":
      return { meetingPlaceholderVisible: true };
    case "visible-with-agenda":
      return { meetingPlaceholderVisible: true, meetingPlaceholderIncludesAgenda: true };
  }
};

export function prepareBoardMeetingForSave(
  fields: BoardMeetingFields,
  data: ProjectJsonData,
): BoardMeeting | undefined {
  if (
    !fields.meetingStartDate.isValid ||
    !fields.meetingStartTime.isValid ||
    !fields.meetingEndTime.isValid
  ) {
    return data.boardMeeting;
  }
  return {
    ...prepareMeetingDateForSave(fields),
    ...prepareMeetingPlaceholderForSave(fields),
    useMeetingNumber: !isBlank(fields.boardMeetingNumber.validatedValue),
    boardMeetingNumber: fields.boardMeetingNumber.validatedValue || undefined,
    boardMeetingForm: fields.boardMeetingForm.value,
    meetingPlace: fields.meetingPlace.value,
    inlineAddress: fields.inlineAddress.validatedValue,
    noticeDescription: fields.noticeDescription.localizedTexts,
  };
}

export type ProjectCompanyRole = {
  companyroleId: CompanyroleId;
  name: string;
  role: RoleType;
};

export type ExtraParticipant = {
  id: string;
  name: string;
  email: string;
};

export function mapRoleToProjectRole(role: CompanyRole): ProjectCompanyRole {
  return {
    companyroleId: role.companyroleId,
    name: getFullName(role),
    role: role.role,
  };
}

export function sortedProjectBoardRoles(roles: ProjectCompanyRole[]) {
  return sortBy(
    roles.filter((r) => isBoardMember(r.role) || isDeputyBoardMember(r.role)),
    (r) => boardRoles.indexOf(r.role),
    (r) => r.name,
  );
}

export function getSortedBoard(context: { companyInfo: CompanyInfo }): ProjectCompanyRole[] {
  return sortedBoard(context.companyInfo.roles).map(mapRoleToProjectRole);
}

export function projectCompanyRoleLabel({
  name,
  role,
}: {
  name: string;
  role?: RoleType;
}): TMessage {
  switch (role) {
    case RoleType.CHAIRPERSON:
      return {
        key: "dbot.boardmeeting.chairpersonOptionLabel",
        params: { name },
      };
    case RoleType.DEPUTY_CHAIRPERSON:
      return {
        key: "dbot.boardmeeting.deputyChairpersonOptionLabel",
        params: { name },
      };
    default: {
      return { translated: name };
    }
  }
}

function getSelectedOrDefault<T extends { companyroleId: CompanyroleId }>({
  options,
  selectedIds,
  defaultValue,
}: {
  options: T[];
  selectedIds?: CompanyroleId[];
  defaultValue?: T[];
}): T[] | undefined {
  if (selectedIds === undefined) {
    return defaultValue;
  }
  return options.filter((o) => selectedIds.includes(o.companyroleId));
}

export function prepProjectCompanyRoleModel(params: {
  options: ProjectCompanyRole[];
  selectedIds?: CompanyroleId[];
  defaultValue?: ProjectCompanyRole[];
}): OptionFieldModel<ProjectCompanyRole> {
  return new OptionFieldModel<ProjectCompanyRole>({
    value: getSelectedOrDefault(params),
    options: params.options,
    idFn: (option) => String(option.companyroleId),
    labelFn: projectCompanyRoleLabel,
  });
}

export function getSelectedBoardRoleIds({
  selectedBoardMembers,
  selectedDeputyMembers,
  selectedObservers,
}: {
  selectedBoardMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedDeputyMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedObservers: OptionFieldModel<ProjectCompanyRole>;
}): CompanyroleId[] {
  return [
    ...selectedBoardMembers.selected.map((m) => m.companyroleId),
    ...selectedDeputyMembers.selected.map((m) => m.companyroleId),
    ...selectedObservers.selected.map((m) => m.companyroleId),
  ];
}

export function toRecipients(
  roles: CompanyRole[],
  {
    selectedBoardMembers,
    selectedDeputyMembers,
    selectedObservers,
    extraParticipants,
  }: {
    selectedBoardMembers: OptionFieldModel<ProjectCompanyRole>;
    selectedDeputyMembers: OptionFieldModel<ProjectCompanyRole>;
    selectedObservers: OptionFieldModel<ProjectCompanyRole>;
    extraParticipants?: ExtraParticipant[];
  },
): Recipient[] {
  return [
    ...selectedBoardMembers.selected.map((m) => ({
      id: m.companyroleId,
      name: m.name,
      email: roles.find((r) => r.companyroleId === m.companyroleId)?.email ?? "",
    })),
    ...selectedDeputyMembers.selected.map((m) => ({
      id: m.companyroleId,
      name: m.name,
      email: roles.find((r) => r.companyroleId === m.companyroleId)?.email ?? "",
    })),
    ...selectedObservers.selected.map((m) => ({
      id: m.companyroleId,
      name: m.name,
      email: roles.find((r) => r.companyroleId === m.companyroleId)?.email ?? "",
    })),
    ...(extraParticipants ?? []).map((m) => ({ id: m.id, name: m.name, email: m.email })),
  ];
}

export function boardMeetingParticipantsLabel({
  board,
  participants,
  extraParticipants,
}: {
  board: ProjectCompanyRole[];
  participants: CompanyroleId[];
  extraParticipants: ExtraParticipant[];
}): TMessage {
  if (
    extraParticipants.length ||
    board.some((b) => isObserver(b) && participants.includes(b.companyroleId))
  ) {
    return { key: "dbot.boardmeeting.participants.the-board-plus-extra" };
  } else if (board.some((b) => isDeputyBoardMember(b) && participants.includes(b.companyroleId))) {
    return { key: "dbot.boardmeeting.participants.the-board-plus-deputies" };
  } else if (board.some((b) => isBoardMember(b) && !participants.includes(b.companyroleId))) {
    return { key: "dbot.boardmeeting.participants.parts-of-the-board" };
  }
  return { key: "dbot.boardmeeting.participants.the-full-board" };
}

export function boardMeetingSignatoriesLabel({
  signersMode,
  selectedSigningBoardMembers,
}: {
  signersMode: SignersMode;
  selectedSigningBoardMembers: CompanyRoleIdWithName[];
}): TMessage {
  switch (signersMode) {
    case SignersMode.ALL:
      return { key: "dbot.boardmeeting.signatories.all" };
    case SignersMode.SELECTED:
      return { translated: selectedSigningBoardMembers.map((m) => m.name).join(", ") };
  }
}
